import React, { useEffect, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { Users, CalendarDays, Percent, AlertCircle, Wallet, ChevronRight, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { whatsappLink, renderWhatsappMessage, computePaymentDisplayStatus, DEFAULT_WHATSAPP_TEMPLATE } from "@/lib/whatsapp";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [openPanel, setOpenPanel] = useState(null); // 'attendance' | 'pending' | null
  const [byClass, setByClass] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [waTemplate, setWaTemplate] = useState(DEFAULT_WHATSAPP_TEMPLATE);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, tpl] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/announcements"),
          api.get("/settings/whatsapp-template"),
        ]);
        setStats(s.data);
        setAnnouncements(a.data.slice(0, 3));
        setWaTemplate(tpl.data.template || DEFAULT_WHATSAPP_TEMPLATE);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const toggleAttendance = async () => {
    if (openPanel === "attendance") return setOpenPanel(null);
    try {
      const { data } = await api.get("/dashboard/attendance-by-class");
      setByClass(data);
      setOpenPanel("attendance");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const togglePending = async () => {
    if (openPanel === "pending") return setOpenPanel(null);
    try {
      const { data } = await api.get("/payments/pending", { params: { month: stats?.current_month } });
      setPendingList(data);
      setOpenPanel("pending");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Visão geral"
        title="Dashboard"
        subtitle="Resumo em tempo real da escola Vôlei Vida. Clique nas métricas para ver detalhes."
        mascot={BRAND.MANGUITO_SAQUE}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="stat-students" label="Alunos Ativos" value={stats?.total_students ?? "—"} icon={Users} color="blue" to="/admin/alunos" />
        <StatCard testId="stat-classes" label="Turmas" value={stats?.total_classes ?? "—"} icon={CalendarDays} color="yellow" to="/admin/turmas" />
        <StatCard testId="stat-attendance" label="Presença Média" value={`${stats?.avg_attendance ?? 0}%`} icon={Percent} color="lime" onClick={toggleAttendance} />
        <StatCard testId="stat-pending" label="Pagamentos Pendentes" value={stats?.pending_payments ?? 0} hint={`mês ${stats?.current_month || ""}`} icon={AlertCircle} color="red" onClick={togglePending} />
      </div>

      {openPanel === "attendance" && (
        <div className="vv-card p-5 mb-6 vv-fade-in" data-testid="attendance-by-class-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Detalhe</div>
              <h3 className="text-xl font-black">Presença por turma</h3>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setOpenPanel(null)}><X className="size-4" /></Button>
          </div>
          {byClass.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhuma turma ainda.</div>
          ) : (
            <div className="space-y-2">
              {byClass.map((c) => (
                <Link
                  key={c.class_id}
                  to="/admin/presenca"
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30 hover:border-[hsl(var(--vv-lime))] transition-all"
                  data-testid={`attendance-class-${c.class_id}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.category} • {c.present}/{c.total} presenças</div>
                  </div>
                  <div className="text-2xl font-black text-[hsl(var(--vv-lime))]">{c.percentage}%</div>
                  <ChevronRight className="size-5 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {openPanel === "pending" && (
        <div className="vv-card p-5 mb-6 vv-fade-in" data-testid="pending-payments-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Detalhe</div>
              <h3 className="text-xl font-black">Pagamentos pendentes</h3>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setOpenPanel(null)}><X className="size-4" /></Button>
          </div>
          {pendingList.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhum pagamento pendente. 🎉</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                    <th className="py-2">Aluno</th>
                    <th className="py-2">Categoria</th>
                    <th className="py-2">Mês</th>
                    <th className="py-2">Valor</th>
                    <th className="py-2">Tel. Responsável</th>
                    <th className="py-2 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingList.map((p) => {
                    const ds = computePaymentDisplayStatus({ status: p.status, month: p.month });
                    const situation = ds === "atrasado" ? "atrasada" : "a vencer";
                    const wa = p.guardian_phone ? whatsappLink(p.guardian_phone, renderWhatsappMessage(waTemplate, {
                      studentName: p.student_name, guardianName: "", month: p.month, amount: p.amount, situation,
                    })) : null;
                    return (
                      <tr key={p.id} className="border-b border-border/60" data-testid={`pending-row-${p.id}`}>
                        <td className="py-2 font-semibold">{p.student_name}</td>
                        <td className="py-2"><span className="vv-chip vv-chip-blue">{p.category || "—"}</span></td>
                        <td className={`py-2 font-bold ${ds === "atrasado" ? "text-[hsl(0_80%_70%)]" : "text-[hsl(var(--vv-yellow))]"}`}>{p.month}</td>
                        <td className="py-2">{p.amount ? `R$ ${Number(p.amount).toFixed(2)}` : "—"}</td>
                        <td className="py-2 text-muted-foreground">{p.guardian_phone || "—"}</td>
                        <td className="py-2 text-right">
                          {wa ? (
                            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid={`dashboard-whatsapp-${p.id}`}>
                              <Button size="sm" variant="outline" className="border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366] hover:text-white">
                                <MessageCircle className="size-3.5 mr-1" /> WhatsApp
                              </Button>
                            </a>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-right">
                <Button asChild size="sm" variant="outline"><Link to="/admin/financeiro">Ir ao Financeiro</Link></Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="vv-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Acessos rápidos</div>
              <h2 className="text-2xl font-black mt-1">Gerencie sua escola</h2>
            </div>
            <img src={BRAND.MANGUITA_FORCA} alt="" className="h-40 hidden sm:block vv-mascot-float" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { to: "/admin/alunos", label: "Cadastrar Aluno", icon: Users },
              { to: "/admin/turmas", label: "Gerenciar Turmas", icon: CalendarDays },
              { to: "/admin/presenca", label: "Registrar Presença", icon: Percent },
              { to: "/admin/financeiro", label: "Ver Financeiro", icon: Wallet },
            ].map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="group flex items-center gap-3 p-4 rounded-lg border border-border bg-muted/30 hover:border-[hsl(var(--vv-lime))] hover:bg-muted transition-all"
              >
                <q.icon className="size-5 text-[hsl(var(--vv-yellow))] group-hover:text-[hsl(var(--vv-lime))] transition-colors" />
                <span className="font-semibold uppercase tracking-wider text-sm">{q.label}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="vv-card p-6">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Mural</div>
          <h2 className="text-xl font-black mb-4">Últimos avisos</h2>
          <div className="space-y-3">
            {announcements.length === 0 && (
              <div className="text-sm text-muted-foreground">Nenhum aviso publicado.</div>
            )}
            {announcements.map((a) => (
              <div key={a.id} className="p-3 rounded-lg bg-muted/40 border border-border">
                <div className="font-semibold">{a.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {new Date(a.created_at).toLocaleDateString("pt-BR")}
                </div>
                <div className="text-sm mt-1 text-muted-foreground line-clamp-2">{a.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
